import { useEffect, useRef } from "react";
import * as THREE from "three";

// Circular sprite texture for glowing nodes
function makeDotTexture() {
  const s = 64;
  const c = document.createElement("canvas");
  c.width = c.height = s;
  const ctx = c.getContext("2d");
  const g = ctx.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
  g.addColorStop(0, "rgba(180,255,225,1)");
  g.addColorStop(0.35, "rgba(16,185,129,0.9)");
  g.addColorStop(1, "rgba(16,185,129,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, s, s);
  const tex = new THREE.CanvasTexture(c);
  return tex;
}

export default function HeroScene() {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const isMobile = window.innerWidth < 768;
    const lowEnd =
      isMobile || (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4);
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const width = mount.clientWidth;
    const height = mount.clientHeight;

    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: !lowEnd, alpha: true, powerPreference: "high-performance" });
    } catch (e) {
      return; // WebGL unavailable — Hero keeps its CSS glow fallback
    }
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, lowEnd ? 1.5 : 2));
    renderer.setSize(width, height);
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 100);
    camera.position.z = 4.6;

    const group = new THREE.Group();
    scene.add(group);

    const EMERALD = 0x10b981;
    const radius = isMobile ? 1.35 : 1.7;
    const detail = lowEnd ? 1 : 2;

    // Network sphere geometry
    const icoGeo = new THREE.IcosahedronGeometry(radius, detail);

    // Edges (connections)
    const wire = new THREE.WireframeGeometry(icoGeo);
    const lineMat = new THREE.LineBasicMaterial({
      color: EMERALD,
      transparent: true,
      opacity: 0,
    });
    const lines = new THREE.LineSegments(wire, lineMat);
    group.add(lines);

    // Nodes (vertices as glowing points)
    const dotTex = makeDotTexture();
    const nodeGeo = new THREE.BufferGeometry();
    nodeGeo.setAttribute("position", icoGeo.getAttribute("position").clone());
    const nodeMat = new THREE.PointsMaterial({
      size: isMobile ? 0.16 : 0.2,
      map: dotTex,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      color: 0xffffff,
    });
    const nodes = new THREE.Points(nodeGeo, nodeMat);
    group.add(nodes);

    // Inner faint sphere for depth
    const innerMat = new THREE.MeshBasicMaterial({
      color: EMERALD,
      wireframe: true,
      transparent: true,
      opacity: 0,
    });
    const inner = new THREE.Mesh(new THREE.IcosahedronGeometry(radius * 0.55, 1), innerMat);
    group.add(inner);

    // Drifting particle constellation
    const P_COUNT = lowEnd ? 260 : 700;
    const pGeo = new THREE.BufferGeometry();
    const pPos = new Float32Array(P_COUNT * 3);
    for (let i = 0; i < P_COUNT; i++) {
      const r = radius * (1.6 + Math.random() * 3.2);
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      pPos[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      pPos[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta) * 0.7;
      pPos[i * 3 + 2] = r * Math.cos(phi);
    }
    pGeo.setAttribute("position", new THREE.BufferAttribute(pPos, 3));
    const pMat = new THREE.PointsMaterial({
      size: 0.045,
      map: dotTex,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      color: 0x8affd6,
    });
    const particles = new THREE.Points(pGeo, pMat);
    scene.add(particles);

    // Interaction state
    let scrollFactor = 0;
    const pointer = { x: 0, y: 0 };
    const cur = { x: 0, y: 0 };

    const onScroll = () => {
      scrollFactor = window.scrollY / Math.max(window.innerHeight, 1);
    };
    const onPointer = (e) => {
      pointer.x = (e.clientX / window.innerWidth) * 2 - 1;
      pointer.y = (e.clientY / window.innerHeight) * 2 - 1;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("pointermove", onPointer, { passive: true });

    const onResize = () => {
      const w = mount.clientWidth;
      const h = mount.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener("resize", onResize);

    // Assemble tween + render loop
    const start = performance.now();
    const ASSEMBLE = 1700;
    let baseSpin = 0;
    let raf;

    const tick = (now) => {
      raf = requestAnimationFrame(tick);
      if (document.hidden) return;

      const t = Math.min((now - start) / ASSEMBLE, 1);
      const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic

      lineMat.opacity = eased * 0.55;
      nodeMat.opacity = eased;
      innerMat.opacity = eased * 0.18;
      pMat.opacity = eased * 0.8;
      const s = 0.15 + eased * 0.85;
      group.scale.setScalar(s);

      if (!reduced) baseSpin += 0.0016;

      // smooth cursor follow
      cur.x += (pointer.x - cur.x) * 0.05;
      cur.y += (pointer.y - cur.y) * 0.05;

      group.rotation.y = baseSpin + scrollFactor * Math.PI * 0.9 + cur.x * 0.4;
      group.rotation.x = scrollFactor * 0.5 + cur.y * 0.3;
      inner.rotation.y = -baseSpin * 1.6;
      inner.rotation.x = baseSpin * 0.8;

      if (!reduced) {
        particles.rotation.y += 0.0004;
        particles.rotation.x += 0.0002;
      }

      renderer.render(scene, camera);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("pointermove", onPointer);
      window.removeEventListener("resize", onResize);
      icoGeo.dispose();
      wire.dispose();
      nodeGeo.dispose();
      pGeo.dispose();
      lineMat.dispose();
      nodeMat.dispose();
      innerMat.dispose();
      pMat.dispose();
      inner.geometry.dispose();
      dotTex.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
    };
  }, []);

  return <div ref={mountRef} className="absolute inset-0 h-full w-full" data-testid="hero-scene" />;
}
